/**
 * NDT invoices (batch 5, fieldiq-api#1166): approved field tickets → NDI-
 * invoices. Typed from routes/ndt-inspection-invoices.routes.ts and
 * lib/ndt-invoice.ts (loadNdtInvoice) on the API's main, camelCase as every
 * /ndt-inspection office read. Hand-written rather than drawn from the API
 * manifest: the manifest records `/invoices/:id` as the probe's 404, and a type
 * is never built from a 4xx body.
 *
 * Money is NUMBER here (the routes Number() it); a tax figure the API holds as
 * NULL stays null and renders `—`.
 */

export const NDT_INVOICE_STATUSES = ['draft', 'sent', 'approved', 'paid', 'void'] as const;
export type NdtInvoiceStatus = (typeof NDT_INVOICE_STATUSES)[number];

/** GET /ndt-inspection/invoices — one register row. */
export interface NdtInvoiceRow {
  id: string;
  invoiceNumber: string;
  status: string;
  currency: string | null;
  /** A calendar day (YYYY-MM-DD). */
  invoiceDate: string | null;
  dueDate: string | null;
  subtotal: number;
  taxAmount: number | null;
  total: number;
  jurisdiction: string | null;
  customerId: string | null;
  customerName: string | null;
  jobId: string | null;
  jobNumber: string | null;
  ticketCount: number;
  sentAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  createdAt: string;
}

/** GET /ndt-inspection/invoices/billable — approved, uninvoiced tickets by job. */
export interface NdtBillableTicket {
  ticketId: string;
  ticketNumber: string;
  scope: string;
  workDate: string | null;
  currency: string | null;
  /** NULL when a line is unpriced — never a stand-in 0. */
  subtotal: number | null;
  poNumber: string | null;
  approvedAt: string | null;
}
export interface NdtBillableJob {
  jobId: string;
  jobNumber: string;
  ticketMode: string | null;
  provinceState: string | null;
  customerId: string;
  customerName: string | null;
  currency: string | null;
  /** The API's sum; NULL when any ticket is unpriced. */
  subtotal: number | null;
  tickets: NdtBillableTicket[];
}

export interface NdtInvoiceTicket {
  id: string;
  ticketNumber: string;
  status: string;
  scope: string;
  workDate: string | null;
  signerName: string | null;
  poNumber: string | null;
  afeNumber: string | null;
  /** Set when a void released the ticket from this invoice. */
  releasedAt: string | null;
}

export interface NdtInvoiceLine {
  id: string;
  ticketId: string;
  ticketNumber: string;
  ticketLineId: string;
  unit: string;
  distanceUnit: string | null;
  method: string | null;
  rateCode: string | null;
  description: string;
  workDate: string | null;
  quantity: number;
  rate: number;
  amount: number;
  sortOrder: number;
}

/** GET /ndt-inspection/invoices/:id (and every write's answer). */
export interface NdtInvoice {
  id: string;
  vertical: 'ndt_inspection';
  invoiceNumber: string;
  status: string;
  currency: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  customerId: string | null;
  customerName: string | null;
  mainContactName: string | null;
  mainContactEmail: string | null;
  accountingEmail: string | null;
  customerAddress: string | null;
  jobId: string | null;
  jobNumber: string | null;
  locationName: string | null;
  lsd: string | null;
  lineRef: string | null;
  projectRef: string | null;
  country: string | null;
  poNumber: string | null;
  afeNumber: string | null;
  costCode: string | null;
  notes: string | null;
  subtotal: number;
  taxRate: number | null;
  taxAmount: number | null;
  total: number;
  jurisdiction: string | null;
  taxRate1Label: string | null;
  taxRate1Percent: number | null;
  taxRate1Amount: number | null;
  taxRate2Label: string | null;
  taxRate2Percent: number | null;
  taxRate2Amount: number | null;
  taxRateSource: string | null;
  taxRateEffectiveFrom: string | null;
  taxFallbackReason: string | null;
  taxExempt: boolean;
  pstExempt: boolean;
  pstExemptNumber: string | null;
  sentAt: string | null;
  sentTo: string | null;
  sentToSource: string | null;
  sentCc: string[] | null;
  paidAt: string | null;
  voidReason: string | null;
  voidedBy: string | null;
  voidedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  tickets: NdtInvoiceTicket[];
  lines: NdtInvoiceLine[];
}

/** POST /ndt-inspection/invoices/generate — a job OR the picked tickets (snake_case write). */
export type NdtInvoiceGenerate =
  | { job_id: string; due_date?: string; notes?: string }
  | { ticket_ids: string[]; due_date?: string; notes?: string };

/** GET /ndt-inspection/invoices/:id/recipient. */
export interface NdtInvoiceRecipient {
  invoiceId: string;
  invoiceNumber: string;
  status: string;
  customerName: string | null;
  to: { name: string | null; email: string; source: string; sourceLabel: string } | null;
  code?: string;
  message?: string;
  lastSent: { at: string; to: string | null; toSource: string | null; cc: string[] | null } | null;
}

export interface NdtInvoiceEnvelope<T> {
  success: boolean;
  data: T;
}
